/* eslint-disable no-nested-ternary */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import { get } from 'lodash';
import { colors } from '../../constants/styleConstants';
import { TP } from '../../constants/consts';
import { useTranslation } from '../../src/i18n';
import CustomAccordion from '../CustomAccordion';
import ProductionAccordionSummary from '../ProductionAccordionSummary';
import ProductionAccordionDescription from '../ProductionAccordionDescription';
import AppCircularProgress from '../AppCircularProgress/AppCircularProgress';
import { getRoutePartWithSlug } from '../../utils/common';
import route from '../../constants/routes';
import EmptyState from '../EmptyState';

const FIRST_ITEM_INDEX = 0;

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  productionCardWrapper: {
    marginBottom: 15,
  },
  loader: {
    justifyContent: 'center',
    marginBottom: 10,
  },
  cancelled: {
    background: colors.lightPink,
  },
  accordionRoot: {
    boxShadow:
      '0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14)',
  },
  archieved: {
    borderLeft: `solid 8px #989898`,
  },
  collapsedCard: {
    border: `solid 1px ${colors.border.grey1}`,
  },
}));

function ProductionAccordion({
  productions,
  hideViewPerformance,
  isProductionsLoading,
  viewType,
  isLoggedIn,
  className,
  page,
  profile,
  initialExpanded = null,
  setGlobalExpanded = () => {},
}) {
  const [expanded, setExpanded] = React.useState(initialExpanded || productions[FIRST_ITEM_INDEX]?.id);
  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
    setGlobalExpanded(isExpanded ? panel : null);
  };

  const classes = useStyles({ expanded });
  const { t } = useTranslation();

  const viewCompanyDetails = company => {
    window.open(`${route.COMPANIES}/${get(company, 'country.slug', '')}/${getRoutePartWithSlug(company)}`);
  };

  return (
    <Grid className={classnames(classes.root, className)}>
      {isProductionsLoading ? (
        <AppCircularProgress className={classes.loader} size="1.5rem" />
      ) : (
        productions?.length > 0 &&
        productions.map(production => {
          const cancelled = production?.performanceTypes?.filter(p => p.slug === 'cancelled').length > 0;

          return (
            <Grid item className={classes.productionCardWrapper} key={`${production.id}-${production.name}`}>
              <CustomAccordion
                handleChange={handleChange}
                expanded={initialExpanded || expanded}
                id={production.id}
                className={classnames(
                  (initialExpanded || expanded) === production?.id && classes.accordionRoot,
                  cancelled && classes.cancelled,
                  production?.isArchived && classes.archieved,
                  expanded !== production?.id && classes.collapsedCard,
                )}
                summary={
                  <ProductionAccordionSummary
                    isExpanded={initialExpanded || expanded}
                    production={production}
                    viewCompanyDetails={viewCompanyDetails}
                    viewType={viewType}
                    cancelled={cancelled}
                    page={page}
                    profile={profile}
                  />
                }
                description={
                  (initialExpanded || expanded) === production?.id && (
                    <ProductionAccordionDescription
                      viewType={viewType}
                      production={production}
                      isLoggedIn={isLoggedIn}
                      cancelled={cancelled}
                      hideViewPerformance={hideViewPerformance}
                      expanded={expanded}
                    />
                  )
                }
                iconType="expand"
                iconPosition="left"
              />
            </Grid>
          );
        })
      )}
    </Grid>
  );
}

ProductionAccordion.propTypes = {
  viewType: PropTypes.string,
  isProductionsLoading: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  productions: PropTypes.array,
  hideViewPerformance: PropTypes.bool,
  className: PropTypes.string,
  initialExpanded: PropTypes.string,
  setGlobalExpanded: PropTypes.func,
};

export default memo(ProductionAccordion);
